import { useEffect, useRef, useState, useCallback } from 'react'
import { PTTEngine, type PeerState } from '../lib/webrtc'
import { useAdapters } from './useAdapters'
import type { RadioChannel, SignalMessage } from '../types/radio'

export interface RadioState {
  activeChannel: RadioChannel | null
  peers: PeerState[]
  transmitting: boolean
  initializing: boolean
  error: string | null
}

export function useRadio() {
  const { radio, auth } = useAdapters()
  const [channels, setChannels] = useState<RadioChannel[]>([])
  const [state, setState] = useState<RadioState>({
    activeChannel: null, peers: [], transmitting: false, initializing: false, error: null,
  })

  const engineRef = useRef<PTTEngine | null>(null)
  const unsubSignalRef = useRef<(() => void) | null>(null)
  const userIdRef = useRef<string | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    auth.getCurrentUser().then((u) => { if (u) userIdRef.current = u.id })
    radio.getChannels().then((ch) => { if (mountedRef.current) setChannels(ch) })
    return () => { mountedRef.current = false; cleanup() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const join = useCallback(async (channel: RadioChannel): Promise<void> => {
    if (!mountedRef.current) return
    cleanup()
    const userId = userIdRef.current
    if (!userId) { setState((s) => ({ ...s, error: 'Не авторизован' })); return }

    setState((s) => ({ ...s, activeChannel: channel, initializing: true, error: null }))

    const engine = new PTTEngine(userId, channel.id, {
      onPeerUpdate: (peers) => { if (mountedRef.current) setState((s) => ({ ...s, peers })) },
      onSpeaking: () => {},
      onTransmitting: (transmitting) => { if (mountedRef.current) setState((s) => ({ ...s, transmitting })) },
      onError: (error) => { if (mountedRef.current) setState((s) => ({ ...s, error, initializing: false })) },
      sendSignal: (partial) => {
        if (!userId) return
        const msg: SignalMessage = { ...partial, type: partial.type as SignalMessage['type'], from: userId, channelId: channel.id }
        void radio.sendSignal(msg)
      },
    })

    engineRef.current = engine

    unsubSignalRef.current = radio.subscribeToSignals(channel.id, async (msg: SignalMessage) => {
      if (msg.from === userId) return
      await engine.handleSignal(msg)
    })

    try {
      await engine.init()
      if (mountedRef.current) setState((s) => ({ ...s, initializing: false }))
    } catch {
      if (mountedRef.current) setState((s) => ({ ...s, initializing: false }))
    }

    void radio.sendSignal({
      type: 'offer', from: userId, to: 'broadcast', channelId: channel.id,
      payload: { action: 'join', userId },
    } as SignalMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radio, auth])

  const leave = useCallback(() => {
    cleanup()
    if (mountedRef.current) setState({ activeChannel: null, peers: [], transmitting: false, initializing: false, error: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startTransmit = useCallback(() => { engineRef.current?.startTransmit() }, [])
  const stopTransmit = useCallback(() => { engineRef.current?.stopTransmit() }, [])

  const createChannel = useCallback(async (name: string): Promise<void> => {
    const ch = await radio.createChannel(name)
    setChannels((prev) => [...prev, ch])
  }, [radio])

  const getAnalyser = useCallback((): AnalyserNode | null => engineRef.current?.getAnalyser() ?? null, [])

  function cleanup() {
    unsubSignalRef.current?.()
    unsubSignalRef.current = null
    engineRef.current?.destroy()
    engineRef.current = null
  }

  return { channels, state, join, leave, startTransmit, stopTransmit, createChannel, getAnalyser }
}
